import { FC, useEffect } from 'react';
import { UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { PropertyFormData } from '@/types/index';
import { NumberInput } from '@components/common/NumberInput';
import { RadioGroup } from '@components/common/RadioGroup';
import { formatNumberSwiss } from '@utils/number-format';

interface Step5FinancesProps {
  setValue: UseFormSetValue<PropertyFormData>;
  watch: UseFormWatch<PropertyFormData>;
  userSettings?: { equityRate: number; acquisitionFees: number };
}

export const Step5Finances: FC<Step5FinancesProps> = ({
  setValue,
  watch,
  userSettings,
}) => {
  const isRented = watch('isRented') || false;
  const rentIncludesCharges = watch('rentIncludesCharges') || false;
  const askingPrice = watch('askingPrice') || 0;
  const downPayment = watch('downPayment');

  // Calcul fonds propres suggérés (pour le helperText)
  const suggestedEquity = userSettings && askingPrice > 0
    ? (askingPrice * userSettings.equityRate / 100) +
      (askingPrice * userSettings.acquisitionFees / 100)
    : null;

  // One-shot: auto-fill downPayment quand userSettings arrive,
  // si askingPrice existe mais pas downPayment (cas édition)
  useEffect(() => {
    if (!userSettings) return;
    const currentPrice = watch('askingPrice') || 0;
    const currentDown = watch('downPayment');
    if (currentPrice > 0 && !currentDown) {
      setValue('downPayment', Math.round(
        currentPrice * (userSettings.equityRate + userSettings.acquisitionFees) / 100
      ));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSettings]);

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
        Finances
      </h2>

      {/* Prix de vente affiché */}
      <div className="flex items-end gap-4">
        <div className="flex-1">
          <NumberInput
            label="Prix de vente affiché"
            value={askingPrice || undefined}
            onChange={(e) => setValue('askingPrice', e.target.value || undefined)}
            onBlur={() => {
              const currentDown = watch('downPayment');
              if (!currentDown && askingPrice > 0 && userSettings) {
                setValue('downPayment', Math.round(
                  askingPrice * (userSettings.equityRate + userSettings.acquisitionFees) / 100
                ));
              }
            }}
            placeholder="440'000"
          />
        </div>
        <span className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">
          CHF
        </span>
      </div>

      {/* Apport en fonds propres (pour calcul ROE) */}
      <div className="flex items-end gap-4">
        <div className="flex-1">
          <NumberInput
            label="Apport en fonds propres"
            value={downPayment || undefined}
            onChange={(e) => setValue('downPayment', e.target.value || undefined)}
            placeholder="100'000"
            helperText={
              suggestedEquity
                ? `Fonds propres suggérés : ${formatNumberSwiss(Math.round(suggestedEquity))} CHF (basé sur ${userSettings?.equityRate}% equity + ${userSettings?.acquisitionFees}% frais)`
                : "Utilisé pour le calcul du ROE (Return on Equity)"
            }
          />
        </div>
        <span className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">
          CHF
        </span>
      </div>

      {/* Charges PPE */}
      <div className="flex items-end gap-4">
        <div className="flex-1">
          <NumberInput
            label="Charges PPE"
            value={watch('ppeCharges') || undefined}
            onChange={(e) => setValue('ppeCharges', e.target.value || undefined)}
            placeholder="400"
          />
        </div>
        <span className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">
          CHF/mois
        </span>
      </div>

      {/* Décomposition charges PPE */}
      <div className="space-y-4 pt-2 border-t border-gray-200 dark:border-gray-700">
        <p className="text-sm text-gray-500 dark:text-gray-400">Détail des charges PPE (informatif)</p>

        <div className="flex items-end gap-4">
          <div className="flex-1">
            <NumberInput
              label="Fonds de rénovation de l'immeuble"
              value={watch('renovationFund') || undefined}
              onChange={(e) => setValue('renovationFund', e.target.value || undefined)}
              placeholder="5'000"
              helperText="Montant versé annuellement au fonds de rénovation de la PPE"
            />
          </div>
          <span className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">
            CHF/an
          </span>
        </div>

        <div className="flex items-end gap-4">
          <div className="flex-1">
            <NumberInput
              label="Assurance incendie de l'immeuble"
              value={watch('buildingInsurance') || undefined}
              onChange={(e) => setValue('buildingInsurance', e.target.value || undefined)}
              placeholder="1'200"
              helperText="Quote-part annuelle de l'assurance incendie de l'immeuble"
            />
          </div>
          <span className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">
            CHF/an
          </span>
        </div>
      </div>

      {/* Actuellement loué */}
      <div className="space-y-4">
        <RadioGroup
          label="Actuellement loué"
          name="isRented"
          options={[
            { value: 'false', label: 'Non' },
            { value: 'true', label: 'Oui' },
          ]}
          value={String(isRented)}
          onChange={(value) => setValue('isRented', value === 'true')}
          layout="horizontal"
        />

        {/* Champs conditionnels si loué = Oui */}
        {isRented && (
          <div className="space-y-4 pl-4 border-l-4 border-blue-300 dark:border-blue-700 ml-2">
            <div className="flex items-end gap-4">
              <div className="flex-1">
                <NumberInput
                  label="Loyer"
                  value={watch('rentPrice') || undefined}
                  onChange={(e) => setValue('rentPrice', e.target.value || undefined)}
                  required={isRented}
                  placeholder="1'800"
                />
              </div>
              <span className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">
                CHF/mois
              </span>
            </div>

            <RadioGroup
              label="Charges incluses dans le loyer"
              name="rentIncludesCharges"
              options={[
                { value: 'false', label: 'Non' },
                { value: 'true', label: 'Oui' },
              ]}
              value={String(rentIncludesCharges)}
              onChange={(value) => setValue('rentIncludesCharges', value === 'true')}
              layout="horizontal"
            />

            {/* Charges facturées au locataire si non incluses */}
            {!rentIncludesCharges && (
              <div className="flex items-end gap-4">
                <div className="flex-1">
                  <NumberInput
                    label="Charges facturées au locataire"
                    value={watch('tenantCharges') || undefined}
                    onChange={(e) => setValue('tenantCharges', e.target.value || undefined)}
                    placeholder="400"
                    helperText="Montant des charges facturées en supplément du loyer (vient s'ajouter au revenu locatif)"
                  />
                </div>
                <span className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">
                  CHF/mois
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
